<template>
  <div
    v-if="choices.length"
    class="dc"
    role="radiogroup"
    :aria-label="ariaLabel"
  >
    <button
      v-for="o in choices"
      :key="o.key"
      type="button"
      role="radio"
      class="dc-choice"
      :class="[`dc-choice--${o.tone}`, { 'is-selected': modelValue === o.key }]"
      :aria-checked="modelValue === o.key ? 'true' : 'false'"
      @click="$emit('update:modelValue', o.key)"
    >
      <span class="dc-choice__icon"><AppIcon :name="o.icon" :size="20" /></span>
      <span class="dc-choice__text">
        <strong>{{ o.label }}</strong>
        <small>{{ o.hint }}</small>
      </span>
    </button>
  </div>
  <p v-else class="dc-empty">
    No further status changes are available for this application.
  </p>
</template>

<script>
import AppIcon from "@/components/shared/AppIcon.vue";

// icon / tone / hint per status key (presentation only)
const META = {
  verified: { icon: "check-circle", tone: "ok", hint: "Documents checked and verified" },
  approved: { icon: "shield-check", tone: "ok", hint: "Approve the membership" },
  rejected: { icon: "x-circle", tone: "bad", hint: "Reject and leave a comment" },
  incomplete: { icon: "alert", tone: "warn", hint: "Send back for corrections" },
};

export default {
  name: "DecisionCards",
  components: { AppIcon },
  props: {
    // [{ key, label }] – the statuses the current role may move to
    options: { type: Array, default: () => [] },
    modelValue: { type: String, default: "" },
    ariaLabel: { type: String, default: "Application status" },
  },
  emits: ["update:modelValue"],
  computed: {
    choices() {
      return (this.options || []).map((o) => ({
        key: o.key,
        label: o.label,
        ...(META[o.key] || { icon: "file", tone: "neutral", hint: "Update the status" }),
      }));
    },
  },
};
</script>

<style scoped>
.dc {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 8px;
}
.dc-choice {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1.5px solid #e3e6ef;
  border-radius: 14px;
  background: #fff;
  text-align: left;
  font: inherit;
  cursor: pointer;
  transition: border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
}
.dc-choice:hover {
  border-color: #f8cfae;
  background: #fffaf6;
}
.dc-choice:focus-visible {
  outline: 3px solid rgba(249, 115, 22, 0.35);
  outline-offset: 2px;
}
.dc-choice__icon {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #fff1e6;
  color: #f97316;
}
.dc-choice__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.dc-choice__text strong {
  font-size: 14px;
  font-weight: 700;
  color: #0b1f4b;
}
.dc-choice__text small {
  font-size: var(--ecu-fs-small);
  color: #5f6579;
}
.dc-choice--ok .dc-choice__icon { background: #e6f6ec; color: #138a3e; }
.dc-choice--bad .dc-choice__icon { background: #fde9e9; color: #c52222; }
.dc-choice--warn .dc-choice__icon { background: #fff6d6; color: #a06a00; }
.dc-choice.is-selected {
  border-color: #f97316;
  background: #fff6ef;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.14);
}
.dc-choice--ok.is-selected { border-color: #16a34a; background: #f3fbf6; box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.14); }
.dc-choice--bad.is-selected { border-color: #dc2f2f; background: #fef5f5; box-shadow: 0 0 0 3px rgba(220, 47, 47, 0.14); }
.dc-choice--warn.is-selected { border-color: #d18a00; background: #fffbea; box-shadow: 0 0 0 3px rgba(209, 138, 0, 0.16); }

.dc-empty {
  margin: 8px 0 0;
  padding: 14px 16px;
  border-radius: 12px;
  background: #f4f6fb;
  font-size: var(--ecu-fs-body);
  color: #5f6579;
}
@media (max-width: 575px) {
  .dc {
    grid-template-columns: minmax(0, 1fr);
  }
}
@media (prefers-reduced-motion: reduce) {
  .dc-choice { transition: none; }
}
</style>